import React, { useMemo, useState, useCallback, useEffect, memo } from "react";
import { Modal, Icon, CircularProgress } from "@ann/web-components";
import { useMediaQueries } from "../../../../../utils/mediaQueries";
import {
  ModalBodyContent,
  FileExplorerContainer,
  FileItemLeft,
  FileItemRight,
  FileName,
  FileItemStyled,
  LoadingContainer,
  NestedContent,
  NestedLoading,
  EmptyStateMessage,
} from "./DrivePickerModal.styled";
import { useDrivesList } from "../../hooks/useDrivesList";
import { useDrivePickerTree } from "./hooks/useDrivePickerTree";

const ICON_CONFIGS = {
  drive: { name: "folder", color: "primary" },
  folder: { name: "folder", color: "primary-alt" },
  pdf: { name: "description", color: "red" },
  docx: { name: "description", color: "blue" },
  doc: { name: "description", color: "blue" },
  xlsx: { name: "description", color: "limeGreen" },
  xls: { name: "description", color: "limeGreen" },
  pptx: { name: "description", color: "orange" },
  ppt: { name: "description", color: "orange" },
  png: { name: "image", color: "primary-alt" },
  jpg: { name: "image", color: "primary-alt" },
  jpeg: { name: "image", color: "primary-alt" },
  default: { name: "description", color: "primary-alt" },
};

const getFileIcon = (type) => ICON_CONFIGS[type] || ICON_CONFIGS.default;

const FileItem = memo(
  ({ file, isExpanded, isSelected, isLoading, onToggle }) => {
    const iconConfig = getFileIcon(file.type);
    const chevronName = isExpanded ? "chevronDown" : "chevronRight";
    const isExpandable = file.type === "folder" || file.type === "drive";

    const handleClick = useCallback(() => {
      onToggle(file);
    }, [onToggle, file]);

    return (
      <FileItemStyled $isActive={isSelected} onClick={handleClick}>
        <FileItemLeft>
          <Icon
            color={iconConfig.color}
            name={iconConfig.name}
            size="medium"
          />
          <FileName title={file.name}>{file.name}</FileName>
        </FileItemLeft>
        <FileItemRight>
          {isExpandable &&
            (isLoading ? (
              <CircularProgress size="small" />
            ) : (
              <Icon
                color="neutral"
                name={chevronName}
                size="medium"
              />
            ))}
        </FileItemRight>
      </FileItemStyled>
    );
  }
);

FileItem.displayName = "FileItem";

const DrivePickerModal = ({ isOpen, onClose, onConfirm }) => {
  const { isMobile, isIpadAirPortrait, isIpadProPortrait } = useMediaQueries();

  const [expandedFolders, setExpandedFolders] = useState({});
  const [selectedFiles, setSelectedFiles] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);

  const { drives, isLoading: isLoadingDrives } =
    useDrivesList(isOpen);

  const {
    treeData,
    loadingFolders,
    loadingDrives,
    isInitialLoading,
    loadDriveContent,
    loadFolderChildren,
    DRIVE_PREFIX,
    FOLDER_PREFIX,
  } = useDrivePickerTree(drives, isOpen);

  useEffect(() => {
    if (!isOpen) {
      setExpandedFolders({});
      setSelectedFiles({});
    }
  }, [isOpen]);

  const handleToggle = useCallback(
    (file) => {
      const isDrive = file.type === "drive";
      const isFolder = file.type === "folder";
      const itemId = file.id;
      const folderUuid = file.folder_uuid || file.id;
      const vaultUuid = file.vault_uuid || file.id;

      if (isDrive) {
        setExpandedFolders((prev) => {
          const isCurrentlyExpanded = !!prev[itemId];
          if (isCurrentlyExpanded) {
            const { [itemId]: removed, ...rest } = prev;
            return rest;
          }
          loadDriveContent(itemId);
          return { ...prev, [itemId]: true };
        });
      } else if (isFolder) {
        setExpandedFolders((prev) => {
          const isCurrentlyExpanded = !!prev[itemId];
          if (isCurrentlyExpanded) {
            const { [itemId]: removed, ...rest } = prev;
            return rest;
          }
          loadFolderChildren(folderUuid, vaultUuid);
          return { ...prev, [itemId]: true };
        });
      } else {
        setSelectedFiles((prev) => {
          const isCurrentlySelected = !!prev[itemId];
          if (isCurrentlySelected) {
            const { [itemId]: removed, ...rest } = prev;
            return rest;
          }
          return { ...prev, [itemId]: true };
        });
      }
    },
    [loadDriveContent, loadFolderChildren]
  );

  const renderDriveLevel = () => {
    if (!drives?.length) return null;
    return drives.map((drive) => {
      const itemId = drive.id;
      const driveKey = `${DRIVE_PREFIX}${itemId}`;
      const isExpanded = !!expandedFolders[itemId];
      const isLoading = !!loadingDrives[itemId];
      const driveData = treeData[driveKey];
      const hasChildren = !!driveData;
      const hasContent =
        driveData &&
        (driveData.folders?.length > 0 || driveData.documents?.length > 0);

      return (
        <React.Fragment key={itemId}>
          <FileItem
            file={{ ...drive, type: "drive" }}
            isExpanded={isExpanded}
            isSelected={false}
            isLoading={isLoading}
            onToggle={handleToggle}
          />
          {isExpanded && (
            <NestedContent $level={0}>
              {isLoading && !hasChildren && (
                <NestedLoading>
                  <CircularProgress size="small" />
                  <span>Loading...</span>
                </NestedLoading>
              )}
              {hasChildren && hasContent &&
                renderTree(driveKey, 1)}
              {hasChildren && !hasContent && !isLoading && (
                <NestedLoading>
                  <span>Empty drive</span>
                </NestedLoading>
              )}
            </NestedContent>
          )}
        </React.Fragment>
      );
    });
  };

  const renderTree = (parentKey, level = 0) => {
    if (level > 10) return null;

    const parentData = treeData[parentKey];
    if (!parentData) return null;

    const { folders = [], documents = [] } = parentData;
    const allItems = [...folders, ...documents];

    if (allItems.length === 0) return null;

    return (
      <>
        {allItems.map((item) => {
          const isFolder = item.type === "folder";
          const itemId = item.id;
          const folderUuid = isFolder ? item.folder_uuid || item.id : null;
          const folderKey = `${FOLDER_PREFIX}${folderUuid}`;

          const isExpanded = isFolder && !!expandedFolders[itemId];
          const isLoading =
            isFolder && folderUuid && !!loadingFolders[folderUuid];
          const isSelected = !isFolder && !!selectedFiles[itemId];
          const hasChildren = isFolder && folderUuid && treeData[folderKey];
          const childData = hasChildren ? treeData[folderKey] : null;
          const hasContent =
            childData &&
            (childData.folders?.length > 0 || childData.documents?.length > 0);

          return (
            <React.Fragment key={itemId}>
              <FileItem
                file={item}
                isExpanded={isExpanded}
                isSelected={isSelected}
                isLoading={isLoading}
                onToggle={handleToggle}
              />
              {isExpanded && isFolder && folderUuid && (
                <NestedContent $level={level}>
                  {isLoading && !hasChildren && (
                    <NestedLoading>
                      <CircularProgress size="small" />
                      <span>Loading...</span>
                    </NestedLoading>
                  )}
                  {hasChildren && hasContent &&
                    renderTree(folderKey, level + 1)}
                  {hasChildren && !hasContent && !isLoading && (
                    <NestedLoading>
                      <span>Empty folder</span>
                    </NestedLoading>
                  )}
                </NestedContent>
              )}
            </React.Fragment>
          );
        })}
      </>
    );
  };

  const modalWidth = useMemo(() => {
    return isMobile || isIpadAirPortrait || isIpadProPortrait ? "95%" : "600px";
  }, [isMobile, isIpadAirPortrait, isIpadProPortrait]);

  const selectedDocumentUuids = useMemo(
    () => Object.keys(selectedFiles),
    [selectedFiles]
  );
  const selectedCount = selectedDocumentUuids.length;

  const findDocumentInTree = useCallback(
    (documentUuid) => {
      for (const key of Object.keys(treeData)) {
        const node = treeData[key];
        const document = node?.documents?.find(
          (doc) =>
            doc.id === documentUuid || doc.document_uuid === documentUuid
        );
        if (document) return document;
      }
      return null;
    },
    [treeData]
  );

  const handleConfirm = useCallback(async () => {
    if (selectedCount === 0 || isProcessing) return;

    setIsProcessing(true);
    try {
      const items = selectedDocumentUuids
        .map((documentUuid) => {
          const document = findDocumentInTree(documentUuid);
          if (!document) return null;

          return {
            document_uuid: document.document_uuid || document.id,
            name: document.name,
            ...(document.file_uuid && { file_uuid: document.file_uuid }),
            ...(document.file_id && { file_id: document.file_id }),
          };
        })
        .filter(Boolean);

      onConfirm?.(items);
      onClose?.();
    } catch (error) {
      // Caller can handle errors via onConfirm or separate error handler
    } finally {
      setIsProcessing(false);
    }
  }, [
    selectedDocumentUuids,
    selectedCount,
    isProcessing,
    findDocumentInTree,
    onConfirm,
    onClose,
  ]);

  const showLoading = isLoadingDrives || isInitialLoading;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="medium" width={modalWidth}>
      <Modal.Header>
        <Modal.Header.Title>AIMS Drive</Modal.Header.Title>
      </Modal.Header>
      <Modal.Body>
        <ModalBodyContent>
          {showLoading ? (
            <LoadingContainer>
              <CircularProgress />
            </LoadingContainer>
          ) : (
            <FileExplorerContainer>
              {drives?.length > 0 ? (
                renderDriveLevel()
              ) : (
                <EmptyStateMessage>
                  No drives found
                </EmptyStateMessage>
              )}
            </FileExplorerContainer>
          )}
        </ModalBodyContent>
      </Modal.Body>
      <Modal.Actions align="end">
        <Modal.Button
          label="Cancel"
          onClick={onClose}
          type="tertiary"
        />
        <Modal.Button
          label={
            isProcessing
              ? "Processing..."
              : `Add Selected (${selectedCount})`
          }
          onClick={handleConfirm}
          type="primary"
          disabled={selectedCount === 0 || isProcessing}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default DrivePickerModal;
